import type { SectionBase } from '@wix/add-panel-common';
import type { SectionNodeInterval, SectionNodeRect } from './useCategoryViewScroll.types';

export const NEXT_SECTION_TOP_POSITION_BUFFER = 25;

interface SectionRectDescriptor {
  id: string;
  rect: SectionNodeRect;
}

export const calculateSectionNodeIntervals = (
  sections: SectionBase[],
  rects: Record<string, SectionNodeRect>,
): SectionNodeInterval[] =>
  sections
    .filter((section) => !section.subNavigationHide)
    .reduce<SectionRectDescriptor[]>(
      (rectDescriptors, { systemId }) =>
        rects[systemId] ? [...rectDescriptors, { id: systemId, rect: rects[systemId] }] : rectDescriptors,
      [],
    )
    .sort(({ rect: { top: top1 } }, { rect: { top: top2 } }) => top1 - top2)
    .reduce<SectionNodeInterval[]>((intervals, rectDescriptor, index, sectionsDescriptors) => {
      const { id: sectionId, rect } = rectDescriptor;
      const isLast = index === sectionsDescriptors.length - 1;

      return [
        ...intervals,
        {
          id: sectionId,
          top: Math.max(rect.top - NEXT_SECTION_TOP_POSITION_BUFFER, 0),
          bottom:
            (isLast ? Number.MAX_SAFE_INTEGER : sectionsDescriptors[index + 1].rect.top) -
            NEXT_SECTION_TOP_POSITION_BUFFER,
        },
      ];
    }, []);

export const calculateSectionDimensions = (
  sectionNodes: Record<string, HTMLElement>,
  additionalOffsetTop: number,
): Record<string, SectionNodeRect> =>
  Object.entries(sectionNodes).reduce((dims, [sectionId, node]) => {
    const { height, top } = node.getBoundingClientRect();

    return {
      ...dims,
      [sectionId]: {
        height,
        top: top - (parseInt(window.getComputedStyle(node).marginTop, 10) || 0) + additionalOffsetTop,
      },
    };
  }, {});

export const getNextSectionIdToSelect = (intervals: SectionNodeInterval[], top: number): string | null | undefined =>
  intervals.find((interval) => top >= interval.top && top < interval.bottom)?.id;
